import React from 'react';
import {Field} from 'redux-form';
import {Form, Row, Col} from 'react-bootstrap';
import {Button, Step, StepContent, StepLabel, Stepper} from '@material-ui/core';
import {isEmpty} from 'lodash/lang';
import * as fields from 'components/common/fields';
import {AutoSuggestSelect} from 'components/common';
import * as validators from 'utils/validators';
import {
  getAvailableRolesOfMessageReceiver,
  makeForm,
  buildUserFullName,
  levelOptions,
  getStateOptions,
  countryOptions,
} from 'utils/common';
import {fetchClassrooms, fetchSchools, fetchUsers} from 'api';
import {
  isKluwellAdmin,
  isParent,
  isReader,
  isTeacher,
  isSchoolCoordinator,
  isSchoolCoordinatorOrTeacher,
} from 'utils/permissions';

class MessageForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeStep: 0,
      messageType: null,
    };
    this.availableRoles = getAvailableRolesOfMessageReceiver(props.me.role);
    this.previousStep = this.previousStep.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.selectMessageType = this.selectMessageType.bind(this);
    this.fetchSchools = this.fetchSchools.bind(this);
    this.fetchSchoolUsers = this.fetchSchoolUsers.bind(this);
    this.handleSchoolChange = this.handleSchoolChange.bind(this);
    this.renderBackButton = this.renderBackButton.bind(this);
    this.renderNextButton = this.renderNextButton.bind(this);
  }

  previousStep() {
    this.setState((prevState) => ({
      activeStep: prevState.activeStep - 1,
      messageType: prevState.activeStep <= 1 ? null : prevState.messageType,
    }));
  }

  nextStep() {
    this.setState((prevState) => ({
      activeStep: prevState.activeStep + 1,
    }));
  }

  selectMessageType(messageType) {
    this.setState({
      activeStep: 1,
      messageType,
    });
  }

  fetchSchools(value) {
    return fetchSchools(value).then(({records}) => records);
  }

  fetchSchoolUsers(value) {
    const {me, receiverRole, receiverSchool} = this.props;
    const extraParams = {queryRoleMode: 'strict'};
    if (!isEmpty(receiverRole)) {
      extraParams.role = receiverRole.value;
    }
    console.log(receiverSchool);
    if (!isEmpty(receiverSchool)) {
      extraParams.schoolId = receiverSchool.id;
    }
    return fetchUsers(value, undefined, undefined, undefined, extraParams).then(({records}) =>
      records
        .map((record) => ({
          id: record.id,
          name: buildUserFullName(record),
        }))
        .filter((record) => record.id !== me.id),
    );
  }

  handleSchoolChange() {
    this.props.clearFields(false, false, 'receivers');
    this.props.clearFields(false, false, 'receiverClassrooms');
  }

  renderBackButton() {
    return (
      <Button variant="contained" className="small-margin" onClick={this.previousStep}>
        Back
      </Button>
    );
  }

  renderNextButton(disabled) {
    return (
      <Button variant="contained" color="primary" className="small-margin" disabled={disabled} onClick={this.nextStep}>
        Next
      </Button>
    );
  }

  render() {
    const {activeStep, messageType} = this.state;
    const {
      country: {value: selectedCountryCode} = {},
      me,
      receiverRole,
      receiverSchool,
      receivers,
      schoolId,
      handleSubmit,
      pristine,
      submitting,
    } = this.props;
    const selectedSchoolId = schoolId ? schoolId : !isEmpty(receiverSchool) && receiverSchool.id;
    const selectedRole = receiverRole ? receiverRole.value : null;
    const backButton = this.renderBackButton();
    const canSelectSchool = isEmpty(schoolId) && !isKluwellAdmin(selectedRole);
    const canSelectClassroom = selectedSchoolId && (isTeacher(selectedRole) || isReader(selectedRole));
    return (
      <Form onSubmit={handleSubmit}>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Select Message Type</StepLabel>
            <StepContent>
              <Button
                variant="contained"
                color="primary"
                className="small-margin"
                onClick={() => this.selectMessageType('individuals')}
              >
                to Individuals
              </Button>
              <Button
                variant="contained"
                color="primary"
                className="small-margin"
                onClick={() => this.selectMessageType('role')}
              >
                to Users of specific Role
              </Button>
            </StepContent>
          </Step>
          <Step>
            <StepLabel>Select Receivers' Role</StepLabel>
            <StepContent>
              <Row>
                <Col md={6}>
                  <Field
                    name="receiverRole"
                    label="Receivers' Role"
                    labelKey="label"
                    valueKey="value"
                    component={fields.Select}
                    options={this.availableRoles}
                    required
                    validate={[validators.isRequired]}
                  />
                </Col>
              </Row>
              {backButton}
              {this.renderNextButton(isEmpty(receiverRole))}
            </StepContent>
          </Step>
          {messageType === null ? (
            <Step>
              <StepLabel>Message Detail</StepLabel>
            </Step>
          ) : messageType === 'individuals' ? (
            <Step>
              <StepLabel>Select Receivers</StepLabel>
              {activeStep === 2 && (
                <StepContent>
                  {canSelectSchool && isKluwellAdmin(me.role) && (
                    <Row>
                      <Col md={6}>
                        <AutoSuggestSelect
                          name="receiverSchool"
                          label="Receiver's School (optional)"
                          labelKey="name"
                          valueKey="id"
                          onSearch={this.fetchSchools}
                          onChange={this.handleSchoolChange}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col key={selectedSchoolId} md={6}>
                      <AutoSuggestSelect
                        name="receivers"
                        label="Receivers"
                        labelKey="name"
                        valueKey="id"
                        onSearch={this.fetchSchoolUsers}
                        multi
                      />
                    </Col>
                  </Row>
                  {backButton}
                  {this.renderNextButton(isEmpty(receivers))}
                </StepContent>
              )}
            </Step>
          ) : (
            messageType === 'role' &&
            !isKluwellAdmin(selectedRole) && (
              <Step>
                <StepLabel>More Filters (optional)</StepLabel>
                <StepContent>
                  {isKluwellAdmin(me.role) && (
                    <Row>
                      <Col md={6}>
                        <AutoSuggestSelect
                          name="receiverSchool"
                          label="Receivers' School"
                          labelKey="name"
                          valueKey="id"
                          onSearch={this.fetchSchools}
                          onChange={this.handleSchoolChange}
                        />
                      </Col>
                    </Row>
                  )}
                  {canSelectClassroom && (isKluwellAdmin(me.role) || isSchoolCoordinatorOrTeacher(me.role)) && (
                    <Row>
                      <Col md={6}>
                        <AutoSuggestSelect
                          name="receiverClassrooms"
                          label="Receivers' Classrooms"
                          labelKey="name"
                          valueKey="id"
                          onSearch={(value) =>
                            fetchClassrooms(value, undefined, undefined, undefined, {schoolId: selectedSchoolId}).then(
                              ({records}) => records,
                            )
                          }
                          multi
                        />
                      </Col>
                    </Row>
                  )}
                  {(isKluwellAdmin(selectedRole) || isSchoolCoordinator(selectedRole)) && !isParent(selectedRole) && (
                    <Row>
                      <Col md={6}>
                        <Field
                          name="level"
                          label="App Level"
                          labelKey="label"
                          valueKey="value"
                          component={fields.Select}
                          options={levelOptions}
                        />
                      </Col>
                    </Row>
                  )}
                  {isKluwellAdmin(me.role) && (
                    <React.Fragment>
                      <Row>
                        <Col md={6}>
                          <Field
                            key={selectedCountryCode}
                            name="state"
                            label="State"
                            labelKey="label"
                            valueKey="value"
                            component={fields.Select}
                            options={getStateOptions(selectedCountryCode)}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Field name="country" label="Country" component={fields.Select} options={countryOptions} />
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                  {backButton}
                  {this.renderNextButton()}
                </StepContent>
              </Step>
            )
          )}
          <Step>
            <StepLabel>Edit Message</StepLabel>
            <StepContent>
              <Row>
                <Col md={12}>
                  <Field name="body" label="Message" component={fields.Textarea} validate={[validators.isRequired]} />
                </Col>
              </Row>
              {backButton}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className="small-margin"
                disabled={pristine || submitting}
              >
                Submit
              </Button>
            </StepContent>
          </Step>
        </Stepper>
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  me: state.account.get('user').toJS(),
});

export default makeForm(MessageForm, 'MessageForm', mapStateToProps, null, [
  'receiverRole',
  'receiverSchool',
  'receivers',
  'country',
]);
